import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';
import { getThemeOptions } from '../themes/seasonalThemes';

const API = process.env.REACT_APP_BACKEND_URL;

export const HomepageThemeSettings = () => {
  const theme = useThemeClasses();
  const [loading, setLoading] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('default');
  const [previewTheme, setPreviewTheme] = useState('default');
  const [saving, setSaving] = useState(false);
  
  const themeOptions = getThemeOptions();
  
  useEffect(() => {
    loadCurrentTheme();
  }, []);
  
  const loadCurrentTheme = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/settings/public`);
      const themeName = response.data?.homepage_theme || 'default';
      setCurrentTheme(themeName);
      setPreviewTheme(themeName);
    } catch (error) {
      console.error('Failed to load theme:', error);
      toast.error('Failed to load current theme');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveTheme = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      
      await axios.put(
        `${API}/api/settings`,
        {
          homepage_settings: {
            homepage_theme: previewTheme
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setCurrentTheme(previewTheme);
      toast.success('Homepage theme updated successfully!');
    } catch (error) {
      console.error('Failed to save theme:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save theme');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className={`flex justify-center items-center py-12 ${theme.card}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return (
    <div className={`${theme.card} rounded-lg shadow-md p-6`}>
      <div className="mb-6">
        <h2 className={`text-2xl font-bold ${theme.textPrimary} mb-2`}>
          🎨 Homepage Theme Settings
        </h2>
        <p className={`${theme.textSecondary} text-sm`}>
          Select a seasonal or holiday theme to customize your homepage background with animated effects.
        </p>
      </div>
      
      {/* Current Theme Display */}
      <div className={`mb-6 p-4 rounded-lg ${theme.bgSecondary} border ${theme.border}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm ${theme.textSecondary} mb-1`}>Current Active Theme:</p>
            <p className={`text-lg font-semibold ${theme.textPrimary}`}>
              {themeOptions.find(t => t.value === currentTheme)?.label || 'Default'}
            </p>
          </div>
          <div className="text-3xl">
            {currentTheme === 'winter' && '❄️'}
            {currentTheme === 'spring' && '🌸'}
            {currentTheme === 'summer' && '☀️'}
            {currentTheme === 'fall' && '🍂'}
            {currentTheme === 'christmas' && '🎄'}
            {currentTheme === 'halloween' && '🎃'}
            {currentTheme === 'thanksgiving' && '🦃'}
            {currentTheme === 'easter' && '🐰'}
            {currentTheme === 'valentines' && '❤️'}
            {currentTheme === 'default' && '🏠'}
          </div>
        </div>
      </div>
      
      {/* Theme Selector */}
      <div className="mb-6">
        <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
          Select Theme
        </label>
        <select
          value={previewTheme}
          onChange={(e) => setPreviewTheme(e.target.value)}
          className={`w-full px-4 py-3 border ${theme.border} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
        >
          {themeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <p className={`mt-2 text-xs ${theme.textSecondary}`}>
          💡 Tip: Choose seasonal themes to match holidays or current season for a festive homepage experience.
        </p>
      </div>
      
      {/* Theme Categories */}
      <div className="mb-6">
        <h3 className={`text-sm font-semibold ${theme.textPrimary} mb-3`}>Available Themes:</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className={`p-3 rounded-lg ${theme.bgSecondary} border ${theme.border}`}>
            <p className={`text-xs font-medium ${theme.textSecondary} mb-1`}>Seasons</p>
            <p className="text-xs">Winter, Spring, Summer, Fall</p>
          </div>
          <div className={`p-3 rounded-lg ${theme.bgSecondary} border ${theme.border}`}>
            <p className={`text-xs font-medium ${theme.textSecondary} mb-1`}>Holidays</p>
            <p className="text-xs">Christmas, Halloween, Thanksgiving</p>
          </div>
          <div className={`p-3 rounded-lg ${theme.bgSecondary} border ${theme.border}`}>
            <p className={`text-xs font-medium ${theme.textSecondary} mb-1`}>Special Days</p>
            <p className="text-xs">Easter, Valentine's Day</p>
          </div>
        </div>
      </div>
      
      {/* Save Button */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <div>
          {previewTheme !== currentTheme && (
            <p className={`text-sm ${theme.textSecondary}`}>
              ⚠️ You have unsaved changes
            </p>
          )}
        </div>
        <button
          onClick={handleSaveTheme}
          disabled={saving || previewTheme === currentTheme}
          className={`px-6 py-2 rounded-lg font-medium transition-colors ${
            previewTheme === currentTheme
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {saving ? 'Saving...' : 'Save Theme'}
        </button>
      </div>
      
      {/* Preview Note */}
      <div className={`mt-4 p-3 rounded-lg ${theme.bgSecondary} border ${theme.border}`}>
        <p className={`text-xs ${theme.textSecondary}`}>
          📌 <strong>Note:</strong> After saving, visit your homepage to see the new theme in action with animated seasonal elements!
        </p>
      </div>
    </div>
  );
};
